/**
 * CRM Contact Detail — profile view with edit, interaction form, skeleton loading.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Tabs, Badge, EmptyState, Button, Skeleton, SkeletonCard } from '../../components/ui';
import { DetailHeader, DetailSection, FieldGrid, Field } from '../../components/detail';
import { Markdown } from '../../components/markdown';
import { Users, Mail, Clock, Building2, Globe, Edit3, Plus } from '../../lib/icons';
import { useT } from '../../lib/i18n';
import { formatDate, timeAgo, safeParse } from '../../lib/utils';
import { fetchContactDetail } from '../../lib/crm-api';
import type { CrmContact, CrmCompany, CrmDeal, CrmInteraction, CrmActivity } from '../../lib/crm-api';
import { ContactFormDialog, InteractionFormDialog } from './forms';
import { DealsList } from './deals-list';

interface Props {
  contactId: number;
}

export function ContactDetailPage({ contactId }: Props) {
  const t = useT();
  const [contact, setContact] = useState<CrmContact | null>(null);
  const [company, setCompany] = useState<CrmCompany | null>(null);
  const [interactions, setInteractions] = useState<CrmInteraction[]>([]);
  const [deals, setDeals] = useState<CrmDeal[]>([]);
  const [activities, setActivities] = useState<CrmActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('interactions');

  // Form state
  const [editOpen, setEditOpen] = useState(false);
  const [interactionOpen, setInteractionOpen] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchContactDetail(contactId);
      setContact(data.contact);
      setCompany(data.company);
      setInteractions(data.interactions);
      setDeals(data.deals);
      setActivities(data.activities);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
          <Skeleton className="h-4 w-32" />
          <div className="flex gap-4">
            <Skeleton className="h-12 w-12 rounded-full" />
            <div className="space-y-2 flex-1">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-64" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        </div>
      </div>
    );
  }

  if (!contact) {
    return <EmptyState icon={Users} title={t('crm.contactNotFound')} description={t('crm.contactNotFoundDesc')} />;
  }

  const tags = safeParse<string[]>(contact.tags, []);

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <DetailHeader
          onBack={() => (window.location.hash = '#/crm/contacts')}
          backLabel={t('crm.backToContacts')}
          icon={
            <div className="w-12 h-12 rounded-full bg-surface-muted flex items-center justify-center flex-shrink-0">
              <span className="text-lg font-bold text-fg-muted">{contact.name.charAt(0).toUpperCase()}</span>
            </div>
          }
          title={contact.name}
          titleSuffix={
            <button
              onClick={() => setEditOpen(true)}
              className="p-1 rounded hover:bg-surface-muted text-fg-faint hover:text-fg-secondary transition-colors"
              title={t('crm.editContact')}
            >
              <Edit3 size={14} />
            </button>
          }
          subtitle={
            <>
              <span className="text-sm text-fg-secondary flex items-center gap-1">
                <Mail size={12} className="text-fg-faint" />
                {contact.email}
              </span>
              {contact.title && <span className="text-sm text-fg-muted">{contact.title}</span>}
              {contact.source && <Badge variant="secondary">{contact.source.replace(/_/g, ' ')}</Badge>}
            </>
          }
          badges={tags.length > 0 ? tags.map((tag) => <Badge key={tag}>{tag}</Badge>) : null}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <DetailSection title="Details">
            <FieldGrid cols={1}>
              <Field label="Phone" value={contact.phone} hideEmpty />
              <Field label="Department" value={contact.department} hideEmpty />
              <Field label="Timezone" value={contact.timezone} hideEmpty />
              <Field label="Language" value={contact.language} hideEmpty />
              <Field
                label="Last Contact"
                value={contact.last_contacted_at ? timeAgo(contact.last_contacted_at) : null}
                hideEmpty
              />
              <Field label="Created" value={formatDate(contact.created_at)} hideEmpty />
              {contact.notes && <Field label="Notes" value={contact.notes} />}
            </FieldGrid>
            {company && (
              <div className="pt-2 border-t border-edge">
                <button
                  onClick={() => (window.location.hash = `#/crm/companies/${company.id}`)}
                  className="flex items-center gap-2 text-xs text-fg-secondary hover:text-fg transition-colors"
                >
                  <Building2 size={12} className="text-fg-faint" />
                  <span className="font-medium">{company.name}</span>
                  {company.country && (
                    <span className="text-fg-faint flex items-center gap-0.5">
                      <Globe size={10} />
                      {company.country}
                    </span>
                  )}
                </button>
              </div>
            )}
          </DetailSection>
          {contact.ai_profile && (
            <DetailSection title="AI Profile">
              <div className="text-sm text-fg-secondary">
                <Markdown content={contact.ai_profile} compact />
              </div>
            </DetailSection>
          )}
        </div>

        {/* Tabs */}
        <Tabs
          tabs={[
            { key: 'interactions', label: `Interactions (${interactions.length})` },
            { key: 'deals', label: `Deals (${deals.length})` },
            { key: 'activities', label: `Activity Log (${activities.length})` },
          ]}
          active={activeTab}
          onChange={setActiveTab}
        />

        {activeTab === 'interactions' && (
          <div className="flex justify-end -mt-3">
            <Button size="sm" variant="ghost" onClick={() => setInteractionOpen(true)}>
              <Plus size={12} />
              <span className="ml-1">Log Interaction</span>
            </Button>
          </div>
        )}

        {activeTab === 'interactions' && <InteractionsSection interactions={interactions} />}
        {activeTab === 'deals' && <DealsList deals={deals} emptyDescription="Quotes and orders will appear here" />}
        {activeTab === 'activities' && <ActivitiesSection activities={activities} />}
      </div>

      {/* Dialogs */}
      <ContactFormDialog open={editOpen} onClose={() => setEditOpen(false)} onSaved={loadData} initial={contact} />
      <InteractionFormDialog
        open={interactionOpen}
        onClose={() => setInteractionOpen(false)}
        onSaved={loadData}
        contactId={contactId}
        companyId={contact.company_id}
      />
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────

function InteractionsSection({ interactions }: { interactions: CrmInteraction[] }) {
  if (interactions.length === 0)
    return <EmptyState icon={Mail} title="No interactions" description="Email and meeting records will appear here" />;
  return (
    <div className="space-y-2">
      {interactions.map((i) => (
        <Card key={i.id} className="p-3">
          <div className="flex items-start gap-2">
            <div className="mt-0.5">
              {i.type.startsWith('email') ? (
                <Mail size={13} className="text-info" />
              ) : (
                <Clock size={13} className="text-fg-muted" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Badge variant={i.type === 'email_inbound' ? 'secondary' : 'default'}>
                  {i.type.replace(/_/g, ' ')}
                </Badge>
                {i.sentiment && (
                  <Badge
                    variant={
                      i.sentiment === 'positive' ? 'success' : i.sentiment === 'negative' ? 'destructive' : 'default'
                    }
                  >
                    {i.sentiment}
                  </Badge>
                )}
                {i.subject && <span className="text-xs text-fg-secondary truncate">{i.subject}</span>}
              </div>
              {i.content_preview && <p className="text-[11px] text-fg-muted mt-1 line-clamp-2">{i.content_preview}</p>}
            </div>
            <span className="text-[10px] text-fg-faint flex-shrink-0">{timeAgo(i.occurred_at)}</span>
          </div>
        </Card>
      ))}
    </div>
  );
}

function ActivitiesSection({ activities }: { activities: CrmActivity[] }) {
  if (activities.length === 0)
    return <EmptyState icon={Clock} title="No activity" description="Activity log will appear here" />;
  return (
    <div className="space-y-1">
      {activities.map((a) => (
        <div key={a.id} className="flex items-start gap-2 py-2 border-b border-edge last:border-b-0">
          <Clock size={12} className="text-fg-faint mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="text-xs text-fg-secondary">
              <span className="font-medium">{a.action.replace(/_/g, ' ')}</span>
              {a.field_name && (
                <span className="text-fg-muted">
                  {' '}
                  — {a.field_name}: {a.old_value || '—'} → {a.new_value || '—'}
                </span>
              )}
            </span>
          </div>
          <span className="text-[10px] text-fg-faint flex-shrink-0">{timeAgo(a.created_at)}</span>
        </div>
      ))}
    </div>
  );
}
